import React, { useEffect, useState } from 'react';
import { useAorText } from 'utils/aorTerminology';
import { useDispatch } from 'react-redux';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewSelect from 'components/DesignSystems/New/StylishNewSelect';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  updateUserName,
  updateUserTimeFormat,
} from 'PREPAREsrc/store/user/user.action';
import { Form } from 'react-bootstrap';
import {
  useUpdateUserTimeFormatMutation,
  useUserTimeFormatQuery,
} from 'hooks/useUserTimeFormat';
import { useUpdateUserProfileSettingsMutation } from 'hooks/useUpdateUserProfileSettings';
import { useAors } from 'hooks/useAors';
import { useUpdateHomeAor } from 'hooks/useUpdateHomeAor';
import {
  useCurrentLocation,
  useUpdateCurrentLocation,
} from 'hooks/useCurrentLocation';
import ProfileCurrentLocation, {
  ProfileLocationDraft,
} from './ProfileCurrentLocation';
import { updateUserProfileSettings } from 'actions/profileActions';
import { useGetRostersForGroup } from 'hooks/incident/useRoster';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { CONTACT_CHANNELS } from 'utils/contactMethods';
import ContactMethodEditor from './ContactMethodEditor';
import {
  ContactMethodDrafts,
  draftErrors,
  emptyDrafts,
  grantsNewSmsConsent,
  toContactMethodInputs,
  toDrafts,
} from './contactMethodDraft';

const timeFormatOptions = [
  {
    value: 'hour24Mode',
    label: '24 Hour Time Format',
  },
  {
    value: 'hour12Mode',
    label: '12 Hour Time Format',
  },
];

type ProfileSettingsProps = {
  user: User;
  focusedUser?: User;
};

type SelectOption = { value: string; label: string };

/** Contact methods are edited outside the form, in their own ordered list. */
type ProfileFormValues = {
  userName: string;
  email: string;
  timeFormat: SelectOption;
  homeAor: SelectOption | null;
};

const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  user,
  focusedUser,
}) => {
  const aorText = useAorText();
  const prepareUserId = user.epic_user_id;

  const { user_guid: userGuid, profile_settings: profileSettings } = user;

  const form = useForm<ProfileFormValues>();
  const { register, formState, control, reset, handleSubmit } = form;

  const reduxDispatch = useDispatch();

  const { data: timeFormat, refetch: refetchTimeFormat } =
    useUserTimeFormatQuery(prepareUserId);
  const updateTimeFormatMutation = useUpdateUserTimeFormatMutation();
  const updateUserProfileSettingsMutation =
    useUpdateUserProfileSettingsMutation();
  const { data: aors = [] } = useAors();
  const updateHomeAorMutation = useUpdateHomeAor();
  const { data: savedLocation } = useCurrentLocation();
  const updateCurrentLocationMutation = useUpdateCurrentLocation();
  const [location, setLocation] = useState<ProfileLocationDraft>({
    label: '',
    latitude: '',
    longitude: '',
  });
  const groupGuid = useAppSelector(selectGroupGuid);
  const { data: roster } = useGetRostersForGroup(groupGuid);

  const currentUserRoster = roster?.find((r) => r.user_guid === userGuid);
  const savedContactMethods = currentUserRoster?.contact_methods;

  const [contacts, setContacts] = useState<ContactMethodDrafts>(emptyDrafts);

  const aorOptions = aors.map((aor) => ({
    value: aor.id,
    label: aor.name,
  }));

  useEffect(() => {
    reset({
      userName: user.user_name,
      email: user.email_address,
      timeFormat:
        timeFormatOptions.find((tF) => tF.value === timeFormat) ||
        timeFormatOptions[0],
      homeAor:
        aorOptions.find((a) => a.value === currentUserRoster?.home_aor) || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, timeFormat, reset, aors, currentUserRoster]);

  useEffect(() => {
    setContacts(toDrafts(savedContactMethods));
  }, [savedContactMethods]);

  useEffect(() => {
    if (!savedLocation) return;
    setLocation({
      label: savedLocation.label ?? '',
      latitude:
        savedLocation.latitude == null ? '' : String(savedLocation.latitude),
      longitude:
        savedLocation.longitude == null ? '' : String(savedLocation.longitude),
    });
  }, [savedLocation]);

  const contactErrors = draftErrors(contacts);
  const willSendWelcomeText = grantsNewSmsConsent(
    contacts,
    savedContactMethods
  );

  const saveProfileSettingsClicked = async (formValues: ProfileFormValues) => {
    const latitudeText = location.latitude.trim();
    const longitudeText = location.longitude.trim();
    if ((latitudeText === '') !== (longitudeText === '')) {
      toast.error(
        'Enter both latitude and longitude, or leave both blank to clear Current Location.'
      );
      return;
    }
    const latitude = latitudeText === '' ? null : Number(latitudeText);
    const longitude = longitudeText === '' ? null : Number(longitudeText);
    if (
      latitude != null &&
      (!Number.isFinite(latitude) ||
        latitude < -90 ||
        latitude > 90 ||
        longitude == null ||
        !Number.isFinite(longitude) ||
        longitude < -180 ||
        longitude > 180)
    ) {
      toast.error(
        'Latitude must be between -90 and 90, and longitude between -180 and 180.'
      );
      return;
    }

    const newSettings = {
      ...profileSettings,
      userName: formValues.userName,
    };

    await updateUserProfileSettingsMutation.mutateAsync({
      userId: userGuid,
      newSettings,
      contactMethods: toContactMethodInputs(contacts),
    });

    await updateHomeAorMutation.mutateAsync({
      homeAor: formValues.homeAor?.value || null,
    });
    await updateCurrentLocationMutation.mutateAsync({
      latitude,
      longitude,
      label: location.label.trim() || null,
    });
    if (!focusedUser) {
      reduxDispatch(updateUserProfileSettings(userGuid, newSettings));
    }

    await updateTimeFormatMutation.mutateAsync({
      userId: prepareUserId,
      timeFormat: formValues.timeFormat?.value,
      name: formValues.userName,
    });

    if (!focusedUser) {
      reduxDispatch(updateUserName(formValues.userName));
      reduxDispatch(updateUserTimeFormat(formValues.timeFormat.value));
      refetchTimeFormat();
    }
    toast.success('User Data Updated Successfully');
  };

  return (
    <FormProvider {...form}>
      <Form onSubmit={handleSubmit(saveProfileSettingsClicked)}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Name</label>
            <StylishNewInput {...register('userName')} />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label">Email</label>
            <StylishNewInput {...register('email')} disabled readOnly />
          </div>
          <div className="col-md-6 d-flex gap-3">
            <div className="col-md-6">
              <label className="form-label">{aorText('Home AOR')}</label>
              <Controller
                control={control}
                name="homeAor"
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={aorOptions}
                    defaultValue={value}
                    value={value}
                    onChange={onChange}
                    placeholder={aorText('Select Home AOR')}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    isDisabled={false}
                  />
                )}
              />
            </div>
            <div className="col-md-6 pe-3">
              <label className="form-label text-truncate d-block">
                Preferred Exercise Time Format
              </label>
              <Controller
                control={control}
                name="timeFormat"
                render={({ field: { onChange, value } }) => (
                  <StylishNewSelect
                    options={timeFormatOptions}
                    defaultValue={value}
                    value={value}
                    onChange={onChange}
                    placeholder={'Select Preferred Exercise Time Format'}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    isDisabled={false}
                  />
                )}
              />
            </div>
          </div>
        </div>
        <div className="row mt-3">
          <ProfileCurrentLocation value={location} onChange={setLocation} />
        </div>
        <hr className="my-4" />

        <div className="row">
          <div className="col-md-8">
            <h5>How we reach you</h5>
            <p>
              Alerts are sent down this list in order, so put the address you
              answer fastest first. Adding a second entry on a channel gives an
              escalation somewhere to go when the first does not answer.
            </p>
          </div>
        </div>

        <div className="row">
          <div className="col-md-10">
            {CONTACT_CHANNELS.map((channel) => (
              <ContactMethodEditor
                key={channel}
                channel={channel}
                methods={contacts[channel]}
                onChange={(methods) =>
                  setContacts((prev) => ({ ...prev, [channel]: methods }))
                }
              />
            ))}
          </div>
        </div>

        <div className="row">
          <div className="col-md-8 mb-3">
            <strong>
              Consent for Communication: switching on consent for a phone number
              gives Pratus explicit permission to send you automated text
              messages and recorded calls about active hazard alerts and
              incident response activations. You can withdraw it here at any
              time, or by replying STOP to any message. Message and data rates
              may apply.
            </strong>
          </div>
        </div>

        {willSendWelcomeText ? (
          <div className="form-text mb-3">
            A confirmation text will be sent to each newly consented number.
          </div>
        ) : null}

        <div className="button-group justify-content-end mt-4">
          <StylishNewButton
            className={'button--primary'}
            type="submit"
            disabled={contactErrors.length > 0}
          >
            Save
          </StylishNewButton>
        </div>
        {contactErrors.map((error) => (
          <span
            className="form-text form-error multiline d-block mt-1"
            key={error}
          >
            {error}
          </span>
        ))}
        {Object.keys(formState.errors).length > 0 && (
          <span className="form-text form-error multiline mt-3">
            Profile settings invalid. Please correct.
          </span>
        )}
      </Form>
    </FormProvider>
  );
};

export default ProfileSettings;
